import reducerFactory from '../store/reducerFactory';

import { EDIT_MODE_ACTION_TYPES, actionCreators } from './editModeActionCreators';
import isSameRow from './isSameRow';
import reconcilePersistedRowsToAdd from './reconcilePersistedRowsToAdd';

export { actionCreators };

export const initialState = {
  clonedRowData: [],
  modifiedRows: [],
  rowsToAdd: [],
  rowsToDelete: [],
};

export const createReducer = getUniqueRowId =>
  reducerFactory({
    actionHandlers: {
      [EDIT_MODE_ACTION_TYPES.RESET_CHANGES]: state => ({
        ...state,
        modifiedRows: [],
        rowsToAdd: [],
        rowsToDelete: [],
      }),
      [EDIT_MODE_ACTION_TYPES.CHANGE_ROW_DATA]: (state, clonedRowData) => ({
        ...state,
        modifiedRows: [],
        rowsToAdd: [],
        rowsToDelete: [],
        clonedRowData,
      }),
      [EDIT_MODE_ACTION_TYPES.CLEAR_ROWS_TO_ADD]: state => ({
        ...state,
        rowsToAdd: [],
      }),
      [EDIT_MODE_ACTION_TYPES.RESTORE_ROWS_TO_ADD]: (state, rowsToAdd) => ({
        ...state,
        rowsToAdd,
      }),
      [EDIT_MODE_ACTION_TYPES.SYNC_ROW_DATA]: (state, clonedRowData) => {
        const hasPendingChanges =
          state.modifiedRows.length > 0 ||
          state.rowsToAdd.length > 0 ||
          state.rowsToDelete.length > 0;

        if (!hasPendingChanges) {
          return {
            ...state,
            modifiedRows: [],
            rowsToAdd: [],
            rowsToDelete: [],
            clonedRowData,
          };
        }

        return {
          ...state,
          clonedRowData,
          rowsToAdd: reconcilePersistedRowsToAdd(state, clonedRowData, getUniqueRowId),
        };
      },
      [EDIT_MODE_ACTION_TYPES.ADD_ROW]: (state, { insertedRow, appendNewRows }) => ({
        ...state,
        rowsToAdd: [
          ...(appendNewRows ? [] : [insertedRow]),
          ...state.rowsToAdd,
          ...(appendNewRows ? [insertedRow] : []),
        ],
      }),
      [EDIT_MODE_ACTION_TYPES.UPDATE_GROUP]: (state, modifiedGroupRows) => {
        const rowCollectionsChanges = {
          modifiedRows: [
            ...state.modifiedRows.filter(row => {
              let isSame = false;
              modifiedGroupRows.forEach(modifiedRow => {
                if (isSameRow(row, modifiedRow, getUniqueRowId)) {
                  isSame = true;
                }
              });
              return !isSame;
            }),
            ...modifiedGroupRows,
          ],
        };

        return {
          ...state,
          ...rowCollectionsChanges,
        };
      },
      [EDIT_MODE_ACTION_TYPES.UPDATE_ROW]: (state, modifiedRow) => {
        const mergeRowUpdate = (existingRow, incomingRow) => ({
          ...existingRow,
          ...incomingRow,
        });

        const addedNodeIndex = state.rowsToAdd.findIndex(row =>
          isSameRow(row, modifiedRow, getUniqueRowId),
        );

        if (addedNodeIndex >= 0) {
          return {
            ...state,
            rowsToAdd: state.rowsToAdd.map((row, index) =>
              index === addedNodeIndex ? mergeRowUpdate(row, modifiedRow) : row,
            ),
          };
        }

        const modifiedRowIndex = state.modifiedRows.findIndex(row =>
          isSameRow(row, modifiedRow, getUniqueRowId),
        );

        if (modifiedRowIndex >= 0) {
          return {
            ...state,
            modifiedRows: state.modifiedRows.map((row, index) =>
              index === modifiedRowIndex ? mergeRowUpdate(row, modifiedRow) : row,
            ),
          };
        }

        const originalRow = state.clonedRowData.find(row =>
          isSameRow(row, modifiedRow, getUniqueRowId),
        );

        return {
          ...state,
          modifiedRows: [...state.modifiedRows, mergeRowUpdate(originalRow ?? {}, modifiedRow)],
        };
      },
      [EDIT_MODE_ACTION_TYPES.DELETE_ROW]: (state, deletedRow) => ({
        ...state,
        rowsToDelete: [
          ...state.rowsToDelete,
          ...(state.rowsToAdd.find(row => isSameRow(row, deletedRow, getUniqueRowId))
            ? []
            : [deletedRow]),
        ],
        rowsToAdd: state.rowsToAdd.filter(
          addedRow => !isSameRow(addedRow, deletedRow, getUniqueRowId),
        ),
      }),
    },
  });
